/* =============================================================================
 * M.M.LaFleur — PDP image zoom.
 *
 * Click a gallery still (grid, stack, carousel or rail) and the viewer opens
 * ALREADY magnified to 2.5x on the spot that was clicked.
 *
 *   Mouse / trackpad  the zoomed photo follows the cursor — move toward an
 *                     edge to see that part of the photo. No click or drag
 *                     needed. Click to fit the whole frame, click again to
 *                     magnify where the cursor is.
 *   Touch             drag to move around; tap to fit / magnify; swipe
 *                     sideways at fit size for the next frame.
 *
 * Arrows / arrow keys step between frames (staying zoomed if you were),
 * Escape closes.
 *
 * Oct 5 2026: opens zoomed (was click-to-open then click-to-zoom), then
 * hover-to-pan on desktop (was click-and-drag). Panning is clamped to the
 * photo itself rather than the letterbox around it.
 * Previous versions: assets/mm-zoom.preopenzoom.js (original),
 * assets/mm-zoom.prehover.js (opens zoomed, drag to pan).
 *
 * Two things shape the implementation:
 *
 *   1. The theme runs Barba page transitions, so the gallery is replaced on
 *      soft navigation. Clicks are therefore delegated from the document, and
 *      a MutationObserver re-decorates galleries as they appear rather than
 *      binding once on load.
 *
 *   2. The gallery's srcset tops out at 1200w, which is a thumbnail once it
 *      fills the viewport. Shopify's CDN takes a `width` query parameter, so
 *      the opener upgrades the largest candidate to 2048 and swaps it in when
 *      it arrives — the thumbnail shows immediately so the click feels instant.
 * ========================================================================== */

(function () {
  "use strict";

  var MAX_SCALE = 2.5;
  var HI_RES = 2048;
  var SWIPE = 50;

  var overlay = null;
  var stage = null;
  var stageImg = null;
  var countNow = null;
  var countAll = null;

  var frames = [];
  var index = 0;
  var scale = 1;
  var tx = 0;
  var ty = 0;
  var aspect = 0;
  var opener = null;
  var pointerId = null;
  var panStart = null;
  var lastX = null;
  var lastY = null;
  var hoverFrame = null;
  var glideTimer = null;

  /* A real hover pointer (mouse, trackpad). Phones and tablets report
     hover: none and keep drag-to-pan. Re-checked on every open, so plugging
     a mouse into a tablet behaves. */
  var finePointer = window.matchMedia ? window.matchMedia("(hover: hover) and (pointer: fine)") : null;
  function hoverMode() { return !!(finePointer && finePointer.matches); }

  /* Full-size files already requested, keyed by URL. Holding the Image objects
     keeps them from being collected, so a neighbour fetched ahead of time is
     decoded and ready the instant you step onto it. */
  var cache = Object.create(null);

  /* --- helpers ---------------------------------------------------------- */

  function largestCandidate(img) {
    var set = img.getAttribute("srcset");
    if (!set) return img.currentSrc || img.src;
    var best = null;
    var bestW = -1;
    set.split(",").forEach(function (part) {
      var bits = part.trim().split(" ").filter(Boolean);
      if (!bits[0]) return;
      var w = parseInt(bits[1] || "0", 10) || 0;
      if (w > bestW) { bestW = w; best = bits[0]; }
    });
    return best || img.currentSrc || img.src;
  }

  function hiRes(img) {
    var from = largestCandidate(img);
    try {
      var u = new URL(from, window.location.href);
      if (u.searchParams.has("width")) {
        u.searchParams.set("width", String(HI_RES));
        return u.href;
      }
      return from;
    } catch (err) {
      return from;
    }
  }

  function preload(url) {
    if (!url) return null;
    if (cache[url]) return cache[url];
    var im = new Image();
    cache[url] = im;
    im.src = url;
    return im;
  }

  function isReady(url) {
    var im = cache[url];
    return !!(im && im.complete && im.naturalWidth > 0);
  }

  function preloadNeighbours() {
    if (frames.length < 2) return;
    [1, -1].forEach(function (delta) {
      var frame = frames[(index + delta + frames.length) % frames.length];
      if (frame) preload(hiRes(frame));
    });
  }

  /* Width / height of a gallery still, from the decoded file if it has one and
     the width/height attributes Shopify prints otherwise. */
  function aspectOf(img) {
    if (img.naturalWidth && img.naturalHeight) return img.naturalWidth / img.naturalHeight;
    var w = parseFloat(img.getAttribute("width"));
    var h = parseFloat(img.getAttribute("height"));
    return w && h ? w / h : 0;
  }

  /* Only stills. Video tiles keep their own controls, and a tile with no <img>
     has nothing to open. */
  function zoomableImages(root) {
    var out = [];
    (root || document).querySelectorAll(".mm-gallery .mm-gallery-item").forEach(function (item) {
      if (item.querySelector("video")) return;
      var img = item.querySelector("img");
      if (img) out.push(img);
    });
    return out;
  }

  function decorate() {
    zoomableImages(document).forEach(function (img) {
      if (img.hasAttribute("data-mm-zoomable")) return;
      img.setAttribute("data-mm-zoomable", "");
      img.setAttribute("tabindex", "0");
      img.setAttribute("role", "button");
      var label = img.getAttribute("alt") || "product image";
      img.setAttribute("aria-label", "Zoom " + label);
    });
  }

  /* --- geometry --------------------------------------------------------- */

  /* The stage image fills the stage with object-fit: contain, so the photo
     itself is a smaller box inside it. Panning is clamped to that box — the
     earlier version clamped to the whole stage, so dragging to an edge of a
     portrait shot showed a wide band of empty letterbox. */
  function photoBox() {
    var W = stageImg.offsetWidth;
    var H = stageImg.offsetHeight;
    var ar = aspect || aspectOf(stageImg);
    if (!ar || !W || !H) return { w: W, h: H, W: W, H: H };
    if (W / H > ar) return { w: H * ar, h: H, W: W, H: H };
    return { w: W, h: W / ar, W: W, H: H };
  }

  function clamp() {
    var b = photoBox();
    var mx = Math.max(0, (b.w * scale - b.W) / 2);
    var my = Math.max(0, (b.h * scale - b.H) / 2);
    tx = Math.min(mx, Math.max(-mx, tx));
    ty = Math.min(my, Math.max(-my, ty));
  }

  function applyTransform() {
    stageImg.style.transformOrigin = "50% 50%";
    stageImg.style.transform = "translate3d(" + tx + "px, " + ty + "px, 0) scale(" + scale + ")";
  }

  function resetZoom() {
    scale = 1;
    tx = 0;
    ty = 0;
    overlay.setAttribute("data-zoomed", "false");
    applyTransform();
  }

  /* Magnify so the photo point (px, py) — measured in unscaled pixels from the
     centre of the stage image — stays where it is on screen. */
  function zoomAt(px, py) {
    scale = MAX_SCALE;
    tx = px * (1 - scale);
    ty = py * (1 - scale);
    clamp();
    overlay.setAttribute("data-zoomed", "true");
    applyTransform();
  }

  /* Zoom on the same part of the photo that was tapped in the gallery, given
     as fractions (0-1) of the gallery image. */
  function zoomAtFraction(fx, fy) {
    var b = photoBox();
    zoomAt((fx - 0.5) * b.w, (fy - 0.5) * b.h);
  }

  /* Hover pan: the cursor's position over the stage picks which part of the
     photo is in view — left edge of the stage shows the photo's left edge,
     and so on. The outer 10% on each side all maps to the very edge, so the
     edges are easy to reach without parking the cursor on the frame line. */
  function panToPointer(clientX, clientY) {
    if (clientX == null || clientY == null) { clamp(); applyTransform(); return; }
    var r = stage.getBoundingClientRect();
    if (!r.width || !r.height) return;
    var fx = (clientX - r.left) / r.width;
    var fy = (clientY - r.top) / r.height;
    fx = Math.min(1, Math.max(0, (fx - 0.1) / 0.8));
    fy = Math.min(1, Math.max(0, (fy - 0.1) / 0.8));
    var b = photoBox();
    var mx = Math.max(0, (b.w * scale - b.W) / 2);
    var my = Math.max(0, (b.h * scale - b.H) / 2);
    tx = mx * (1 - 2 * fx);
    ty = my * (1 - 2 * fy);
    applyTransform();
  }

  /* The fit-to-zoom move gets the long ease; ordinary hover tracking uses a
     short one (mm-zoom.css). data-gliding marks the long one. */
  function glide() {
    overlay.setAttribute("data-gliding", "true");
    if (glideTimer) window.clearTimeout(glideTimer);
    glideTimer = window.setTimeout(function () {
      overlay.setAttribute("data-gliding", "false");
    }, 340);
  }

  function zoomToPointer(clientX, clientY) {
    scale = MAX_SCALE;
    overlay.setAttribute("data-zoomed", "true");
    glide();
    panToPointer(clientX, clientY);
  }

  function toggleZoom(e) {
    if (scale > 1) { glide(); resetZoom(); return; }
    if (hoverMode()) { zoomToPointer(e.clientX, e.clientY); return; }
    var box = stageImg.getBoundingClientRect();
    glide();
    zoomAt(e.clientX - (box.left + box.width / 2), e.clientY - (box.top + box.height / 2));
  }

  /* Re-apply the current view after the layout or the photo changed. */
  function refit() {
    if (scale > 1 && hoverMode()) panToPointer(lastX, lastY);
    else { clamp(); applyTransform(); }
  }

  /* --- overlay ---------------------------------------------------------- */

  function icon(paths) {
    return '<svg class="mm-icon" viewBox="0 0 24 24" aria-hidden="true" fill="none" ' +
      'stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">' + paths + "</svg>";
  }

  function build() {
    if (overlay) return;
    overlay = document.createElement("div");
    overlay.className = "mm-zoom";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", "Product image viewer");
    overlay.hidden = true;
    overlay.innerHTML =
      '<div class="mm-zoom-scrim" data-mm-zoom-close></div>' +
      '<button type="button" class="mm-zoom-close" data-mm-zoom-close aria-label="Close">' +
      icon('<path d="M18 6 6 18"/><path d="m6 6 12 12"/>') + "</button>" +
      '<button type="button" class="mm-zoom-prev" aria-label="Previous image">' +
      icon('<path d="m15 18-6-6 6-6"/>') + "</button>" +
      '<button type="button" class="mm-zoom-next" aria-label="Next image">' +
      icon('<path d="m9 18 6-6-6-6"/>') + "</button>" +
      '<figure class="mm-zoom-stage"><img alt=""></figure>' +
      '<p class="mm-zoom-count"><span data-now>1</span> / <span data-all>1</span></p>';

    document.body.appendChild(overlay);
    stage = overlay.querySelector(".mm-zoom-stage");
    stageImg = stage.querySelector("img");
    countNow = overlay.querySelector("[data-now]");
    countAll = overlay.querySelector("[data-all]");

    overlay.querySelector(".mm-zoom-scrim").addEventListener("click", close);
    overlay.querySelector(".mm-zoom-close").addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      close();
    });
    overlay.querySelector(".mm-zoom-prev").addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      step(-1);
    });
    overlay.querySelector(".mm-zoom-next").addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      step(1);
    });

    stageImg.addEventListener("click", function (e) {
      /* A drag or swipe ends with a click event; ignore it so letting go
         doesn't also toggle the zoom. */
      if (panStart && panStart.moved) return;
      toggleZoom(e);
    });

    /* The photo's real proportions are only certain once a file decodes; keep
       the pan limits honest when the preview or the 2048 lands. */
    stageImg.addEventListener("load", function () {
      if (!aspect) aspect = aspectOf(stageImg);
      refit();
    });

    stageImg.addEventListener("pointerdown", onPointerDown);
    stageImg.addEventListener("pointermove", onPointerMove);
    stageImg.addEventListener("pointerup", onPointerUp);
    stageImg.addEventListener("pointercancel", onPointerUp);

    /* Mouse: follow the cursor. Tracked on the whole overlay so the last
       position is known even when the cursor sits over an arrow, and the
       view is updated once per frame however fast the events arrive. */
    overlay.addEventListener("pointermove", function (e) {
      if (e.pointerType !== "mouse") return;
      lastX = e.clientX;
      lastY = e.clientY;
      if (!isOpen() || scale === 1 || !hoverMode()) return;
      if (hoverFrame) return;
      hoverFrame = window.requestAnimationFrame(function () {
        hoverFrame = null;
        panToPointer(lastX, lastY);
      });
    });

    /* No wheel panning: on a trackpad it fought the hover position. The
       page behind is locked (mm-no-scroll), so the wheel does nothing. */
    stage.addEventListener("wheel", function (e) {
      if (isOpen()) e.preventDefault();
    }, { passive: false });

    window.addEventListener("resize", function () {
      if (isOpen()) refit();
    }, { passive: true });
  }

  function render() {
    var img = frames[index];
    if (!img) return;

    resetZoom();
    aspect = aspectOf(img);
    overlay.setAttribute("data-single", frames.length < 2 ? "true" : "false");
    countNow.textContent = String(index + 1);
    countAll.textContent = String(frames.length);
    stageImg.alt = img.getAttribute("alt") || "";

    var full = hiRes(img);
    var wanted = index;

    if (isReady(full)) {
      stageImg.src = full;
      overlay.setAttribute("data-loading", "false");
      preloadNeighbours();
      return;
    }

    stageImg.src = img.currentSrc || img.src;
    if (full === stageImg.src) {
      overlay.setAttribute("data-loading", "false");
      preloadNeighbours();
      return;
    }

    overlay.setAttribute("data-loading", "true");
    var pending = preload(full);
    var settle = function () {
      if (wanted !== index || !overlay || overlay.hidden) return;
      stageImg.src = full;
      overlay.setAttribute("data-loading", "false");
      preloadNeighbours();
    };
    if (pending.complete && pending.naturalWidth > 0) { settle(); return; }
    pending.addEventListener("load", settle, { once: true });
    pending.addEventListener("error", function () {
      overlay.setAttribute("data-loading", "false");
      preloadNeighbours();
    }, { once: true });
  }

  /* --- pointer: pan when magnified, swipe between frames when fitted ------ */

  function onPointerDown(e) {
    /* Mouse pans by hovering, so a press is only ever a click. */
    if (e.pointerType === "mouse") return;
    pointerId = e.pointerId;
    panStart = {
      x: e.clientX, y: e.clientY, tx: tx, ty: ty, moved: false,
      mode: scale > 1 ? "pan" : "swipe"
    };
    if (panStart.mode === "pan") overlay.setAttribute("data-panning", "true");
    stageImg.setPointerCapture(pointerId);
  }

  function onPointerMove(e) {
    if (!panStart || e.pointerId !== pointerId) return;
    var dx = e.clientX - panStart.x;
    var dy = e.clientY - panStart.y;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) panStart.moved = true;
    if (panStart.mode !== "pan") return;
    /* 1:1 with the pointer — the photo moves exactly as far as the finger. */
    tx = panStart.tx + dx;
    ty = panStart.ty + dy;
    clamp();
    applyTransform();
  }

  function onPointerUp(e) {
    if (!panStart || e.pointerId !== pointerId) return;
    overlay.setAttribute("data-panning", "false");
    if (stageImg.hasPointerCapture(pointerId)) stageImg.releasePointerCapture(pointerId);
    pointerId = null;

    if (panStart.mode === "swipe" && panStart.moved) {
      var dx = e.clientX - panStart.x;
      var dy = e.clientY - panStart.y;
      if (Math.abs(dx) > SWIPE && Math.abs(dx) > Math.abs(dy)) step(dx < 0 ? 1 : -1);
    }
    /* Cleared after the click event has had its chance to fire. */
    window.setTimeout(function () { panStart = null; }, 0);
  }

  /* Stepping keeps whatever zoom state you were in: zoomed frames open zoomed
     on the middle of the next photo, fitted frames stay fitted. */
  function step(delta) {
    if (frames.length < 2) return;
    var wasZoomed = scale > 1;
    index = (index + delta + frames.length) % frames.length;
    render();
    if (wasZoomed) {
      window.requestAnimationFrame(function () {
        if (hoverMode() && lastX != null) zoomToPointer(lastX, lastY);
        else { glide(); zoomAtFraction(0.5, 0.5); }
      });
    }
  }

  function open(img, fx, fy, clientX, clientY) {
    build();
    overlay.setAttribute("data-hover", hoverMode() ? "true" : "false");
    if (clientX != null) { lastX = clientX; lastY = clientY; }
    frames = zoomableImages(document);
    index = frames.indexOf(img);
    if (index < 0) { frames = [img]; index = 0; }

    opener = img;
    overlay.hidden = false;
    document.body.classList.add("mm-no-scroll");
    render();
    preloadNeighbours();

    /* Next frame: fade the overlay in and glide from fitted to magnified on the
       point that was tapped, so it reads as "zooming into" the photo. */
    window.requestAnimationFrame(function () {
      overlay.setAttribute("data-open", "true");
      /* Mouse: zoom straight onto the cursor's position over the viewer, so
         the first nudge of the mouse continues from what is on screen
         instead of jumping. Touch / keyboard: the spot tapped in the gallery. */
      if (hoverMode() && clientX != null) zoomToPointer(clientX, clientY);
      else { glide(); zoomAtFraction(fx, fy); }
    });
    overlay.querySelector(".mm-zoom-close").focus();
  }

  function close() {
    if (!overlay || overlay.hidden) return;
    overlay.setAttribute("data-open", "false");
    document.body.classList.remove("mm-no-scroll");
    window.setTimeout(function () {
      if (overlay.getAttribute("data-open") === "false") overlay.hidden = true;
    }, 240);
    if (opener && document.contains(opener)) opener.focus();
    opener = null;
  }

  function isOpen() {
    return overlay && !overlay.hidden && overlay.getAttribute("data-open") === "true";
  }

  /* --- wiring ----------------------------------------------------------- */

  function fractionIn(img, e) {
    var r = img.getBoundingClientRect();
    if (!r.width || !r.height || typeof e.clientX !== "number") return [0.5, 0.5];
    return [
      Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))
    ];
  }

  document.addEventListener("click", function (e) {
    var img = e.target.closest(".mm-gallery .mm-gallery-item img");
    if (!img) return;
    if (e.target.closest(".mm-shop-look-btn")) return;
    if (img.closest(".mm-gallery-item").querySelector("video")) return;
    e.preventDefault();
    var f = fractionIn(img, e);
    /* A keyboard-activated click reports 0,0 — treat it as no pointer. */
    var hasPoint = e.detail > 0 && (e.clientX || e.clientY);
    open(img, f[0], f[1], hasPoint ? e.clientX : null, hasPoint ? e.clientY : null);
  });

  document.addEventListener("keydown", function (e) {
    var focused = document.activeElement;
    if (!isOpen() && focused && focused.matches && focused.matches("img[data-mm-zoomable]")) {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(focused, 0.5, 0.5); }
      return;
    }
    if (!isOpen()) return;

    if (e.key === "Escape") { e.preventDefault(); close(); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
    else if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
    else if (e.key === "Tab") {
      var stops = Array.prototype.filter.call(
        overlay.querySelectorAll("button:not([disabled])"),
        function (b) {
          return b.getClientRects().length > 0 && window.getComputedStyle(b).visibility !== "hidden";
        }
      );
      if (!stops.length) { e.preventDefault(); return; }
      var idx = stops.indexOf(document.activeElement);
      if (e.shiftKey && idx <= 0) { e.preventDefault(); stops[stops.length - 1].focus(); }
      else if (!e.shiftKey && (idx === -1 || idx === stops.length - 1)) { e.preventDefault(); stops[0].focus(); }
    }
  });

  function init() {
    decorate();
    var observer = new MutationObserver(function () { decorate(); });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
}());
